import React, { useEffect, useId, useState } from "react";
import { Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from "@mui/material";

function toCssSize(value) {
  return typeof value === "number" ? `${value}px` : value;
}

export default function AppMenu({
  renderTrigger,
  sections = [],
  disabled = false,
  menuLabel,
  minWidth = 260,
  anchorOrigin = { vertical: "bottom", horizontal: "right" },
  transformOrigin = { vertical: "top", horizontal: "right" },
  className = "",
  onOpen,
  onClose,
}) {
  const generatedId = useId();
  const triggerId = `${generatedId}-trigger`;
  const menuId = `${generatedId}-menu`;
  const [anchorElement, setAnchorElement] = useState(null);
  const open = Boolean(anchorElement);
  const visibleSections = sections.filter((section) => section?.items?.length);
  const unavailable = disabled || !visibleSections.length;

  useEffect(() => {
    if (unavailable) {
      setAnchorElement(null);
    }
  }, [unavailable]);

  function openMenu(event) {
    if (unavailable) {
      return;
    }

    setAnchorElement(event.currentTarget);
    onOpen?.(event);
  }

  function closeMenu(event, reason) {
    setAnchorElement(null);
    onClose?.(event, reason);
  }

  function handleTriggerClick(event) {
    if (open) {
      closeMenu(event, "triggerClick");
      return;
    }

    openMenu(event);
  }

  function handleTriggerKeyDown(event) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      openMenu(event);
    }
  }

  function handleItemClick(item, event) {
    closeMenu(event, "itemSelect");
    item.onClick?.(event);
  }

  const triggerProps = {
    id: triggerId,
    disabled: unavailable,
    "aria-haspopup": "menu",
    "aria-expanded": open ? "true" : "false",
    "aria-controls": open ? menuId : undefined,
    onClick: handleTriggerClick,
    onKeyDown: handleTriggerKeyDown,
  };

  const paperClasses = ["ds-menu__paper", className].filter(Boolean).join(" ");

  return (
    <>
      {renderTrigger?.({ open, triggerProps })}

      <Menu
        id={menuId}
        anchorEl={anchorElement}
        open={open}
        onClose={closeMenu}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        MenuListProps={{
          dense: true,
          className: "ds-menu__list",
          "aria-label": menuLabel,
          "aria-labelledby": menuLabel ? undefined : triggerId,
        }}
        PaperProps={{
          className: paperClasses,
          style: { "--ds-menu-min-width": toCssSize(minWidth) },
        }}
      >
        {visibleSections.map((section, sectionIndex) => (
          <React.Fragment key={section.key ?? section.title ?? sectionIndex}>
            {section.dividerBefore && sectionIndex > 0 ? <Divider className="ds-menu__divider" /> : null}
            {section.title ? (
              <ListSubheader disableSticky className="ds-menu__subheader">
                {section.title}
              </ListSubheader>
            ) : null}
            {section.items.map((item, itemIndex) => {
              const ItemIcon = item.icon;
              const itemClasses = [
                "ds-menu__item",
                item.tone === "danger" ? "ds-menu__item--danger" : "",
                item.className ?? "",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <MenuItem
                  key={item.key ?? item.label ?? itemIndex}
                  className={itemClasses}
                  disabled={item.disabled}
                  onClick={(event) => handleItemClick(item, event)}
                >
                  {ItemIcon ? (
                    <ListItemIcon>
                      <ItemIcon sx={{ fontSize: 18 }} aria-hidden="true" />
                    </ListItemIcon>
                  ) : null}
                  <ListItemText primary={item.label} />
                </MenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </Menu>
    </>
  );
}
